/**
 * @architecture-header 1
 * @layer module
 * @domain activities
 * @responsibility Own the high-frequency student answering state, per-question answers and tutor thread.
 * @state-owner module-scoped Zustand
 * @allowed-imports Zustand; shared view types and demo constants; shared transient reset registry
 * @forbidden API entities; permissions; tokens; routes; app bootstrap
 * @public-api activities module and the workspace tutor thread
 * @side-effects in-memory UI updates
 * @tests module-contracts.test.ts
 */

import { create } from 'zustand';
import { INITIAL_THREAD } from '../../../shared/lib/demo-data';
import type { ThreadMessage } from '../../../shared/lib/model';
import { registerWorkspaceTransientReset } from '../../../shared/lib/transient-reset';

interface StudentUiState {
  question: number;
  /** The option chosen per question index, so the jump panel can mark answered questions. */
  answers: Readonly<Record<number, number>>;
  threadOpen: boolean;
  typing: boolean;
  thread: readonly ThreadMessage[];
  tries: number;
  escalated: boolean;
  teacherHandoff: boolean;

  setQuestion(index: number): void;
  selectOption(question: number, option: number): void;
  toggleThread(): void;
  openThread(): void;
  setTyping(typing: boolean): void;
  pushMessage(message: ThreadMessage): void;
  registerTry(): number;
  escalate(): void;
  reset(): void;
}

function initial() {
  return {
    question: 0,
    answers: {} as Readonly<Record<number, number>>,
    threadOpen: false,
    typing: false,
    thread: INITIAL_THREAD,
    tries: 0,
    escalated: false,
    teacherHandoff: false,
  };
}

export const useStudentUiStore = create<StudentUiState>((set, get) => ({
  ...initial(),

  setQuestion: (question) => set({ question }),
  selectOption: (question, option) =>
    set((state) => ({ answers: { ...state.answers, [question]: option } })),
  toggleThread: () => set((state) => ({ threadOpen: !state.threadOpen })),
  openThread: () => set({ threadOpen: true }),
  setTyping: (typing) => set({ typing }),
  pushMessage: (message) => set((state) => ({ thread: [...state.thread, message] })),
  registerTry: () => {
    const tries = get().tries + 1;
    set({ tries });
    return tries;
  },
  escalate: () => set({ escalated: true, teacherHandoff: true }),
  reset: () => set({ ...initial() }),
}));

registerWorkspaceTransientReset('student-ui', () => useStudentUiStore.getState().reset());
